(() => {
  'use strict';
  const init = root => {
    if (!root || root.dataset.fpReady) return;
    root.dataset.fpReady = 'true';
    // Keep the bundled clip self-contained; an optional Shopify video replaces it.
    const preview = root.querySelector('[data-fp-product-video]');
    if (preview) {
      const motion = preview.querySelector('[data-fp-motion-image]');
      const toggle = preview.querySelector('[data-fp-motion-toggle]');
      const video = preview.querySelector('video');
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const events = new AbortController();
      let wantsPlayback = preview.dataset.autoplay === 'true' && !reducedMotion.matches;
      let inView = !('IntersectionObserver' in window);
      let animating = false;
      const updatePlayback = () => {
        const playing = wantsPlayback && inView && !document.hidden;
        if (motion && playing !== animating) {
          motion.src = playing ? motion.dataset.motionSrc : motion.dataset.posterSrc;
          animating = playing;
        }
        if (toggle) {
          toggle.setAttribute('aria-pressed', String(playing));
          toggle.setAttribute('aria-label', playing ? 'Pause product preview' : 'Play product preview');
          toggle.querySelector('[data-fp-motion-label]').textContent = playing ? 'Pause preview' : 'Play preview';
        }
        if (video) {
          if (playing) video.play().catch(() => {});
          else video.pause();
        }
      };
      if (toggle) {
        toggle.hidden = false;
        toggle.addEventListener('click', () => { wantsPlayback = !wantsPlayback; updatePlayback(); }, { signal: events.signal });
      }
      if (video) {
        video.addEventListener('play', () => { wantsPlayback = true; }, { signal: events.signal });
        video.addEventListener('pause', () => { if (inView && !document.hidden) wantsPlayback = false; }, { signal: events.signal });
      }
      const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        inView = entries[0].isIntersecting;
        updatePlayback();
      }, { threshold: 0.2 }) : null;
      observer?.observe(preview);
      reducedMotion.addEventListener('change', () => { if (reducedMotion.matches) { wantsPlayback = false; updatePlayback(); } }, { signal: events.signal });
      document.addEventListener('visibilitychange', updatePlayback, { signal: events.signal });
      document.addEventListener('shopify:section:unload', event => {
        if (!event.target.contains(root)) return;
        observer?.disconnect();
        events.abort();
        video?.pause();
      }, { signal: events.signal });
      updatePlayback();
    }
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const tabs = Array.from(root.querySelectorAll('[data-fp-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-fp-panel]'));
    const chooseTab = (tab, focus = false) => {
      tabs.forEach(t => { const selected=t===tab; t.setAttribute('aria-selected',String(selected)); t.tabIndex=selected?0:-1; });
      panels.forEach(p => { p.hidden=p.dataset.fpPanel!==tab.dataset.fpTab; });
      if (focus) tab.focus();
    };
    tabs.forEach((tab,index) => {
      tab.addEventListener('click',()=>chooseTab(tab));
      tab.addEventListener('keydown',e=>{
        let next;
        if(e.key==='ArrowRight')next=(index+1)%tabs.length;
        if(e.key==='ArrowLeft')next=(index-1+tabs.length)%tabs.length;
        if(e.key==='Home')next=0;
        if(e.key==='End')next=tabs.length-1;
        if(next!==undefined){e.preventDefault();chooseTab(tabs[next],true);}
      });
    });
    const dialog=root.querySelector('[data-fp-dialog]');
    const image=root.querySelector('[data-fp-dialog-image]');
    const zoom=root.querySelector('[data-fp-zoom-toggle]');
    let opener,oldOverflow;
    root.querySelectorAll('[data-fp-preview]').forEach(button=>button.addEventListener('click',()=>{
      const source=button.querySelector('img');
      if(!source||!dialog||!image||typeof dialog.showModal!=='function')return;
      opener=button;oldOverflow=document.body.style.overflow;
      image.src=source.currentSrc||source.src;image.alt=source.alt;image.classList.remove('is-expanded');
      image.width=source.naturalWidth||source.width;image.height=source.naturalHeight||source.height;
      root.querySelector('[data-fp-dialog-title]').textContent=source.alt||'Product preview';
      zoom.setAttribute('aria-pressed','false');zoom.textContent='Zoom in';
      dialog.showModal();document.body.style.overflow='hidden';root.querySelector('.fp-dialog-body').scrollTo(0,0);
    }));
    root.querySelector('[data-fp-dialog-close]')?.addEventListener('click',()=>dialog.close());
    dialog?.addEventListener('click',e=>{if(e.target===dialog)dialog.close();});
    dialog?.addEventListener('close',()=>{document.body.style.overflow=oldOverflow||'';opener?.focus({preventScroll:true});});
    zoom?.addEventListener('click',()=>{const expanded=image.classList.toggle('is-expanded');zoom.setAttribute('aria-pressed',String(expanded));zoom.textContent=expanded?'Fit image':'Zoom in';});

    const track=root.querySelector('[data-fp-review-track]');
    const prev=root.querySelector('[data-fp-review-prev]');
    const next=root.querySelector('[data-fp-review-next]');
    const position=root.querySelector('[data-fp-review-position]');
    const cards=Array.from(track?.children||[]);
    const step=()=>cards.length?cards[0].getBoundingClientRect().width+parseFloat(getComputedStyle(track).gap||0):0;
    const updateReviews=()=>{
      if(!track||!cards.length)return;
      const distance=step();const first=Math.min(cards.length,Math.round(track.scrollLeft/distance)+1);
      const visible=Math.max(1,Math.floor((track.clientWidth+parseFloat(getComputedStyle(track).gap||0))/distance));
      const last=Math.min(cards.length,first+visible-1);
      position.textContent=first===last?`${first} of ${cards.length}`:`${first}–${last} of ${cards.length}`;
      prev.disabled=track.scrollLeft<2;next.disabled=track.scrollLeft+track.clientWidth>=track.scrollWidth-2;
    };
    const move=direction=>track?.scrollBy({left:direction*step(),behavior});
    prev?.addEventListener('click',()=>move(-1));next?.addEventListener('click',()=>move(1));
    let frame;
    track?.addEventListener('scroll',()=>{cancelAnimationFrame(frame);frame=requestAnimationFrame(updateReviews);},{passive:true});
    track?.addEventListener('keydown',e=>{if(e.key==='ArrowRight'||e.key==='ArrowLeft'){e.preventDefault();move(e.key==='ArrowRight'?1:-1);}});
    if(track){new ResizeObserver(updateReviews).observe(track);updateReviews();}

    const forms=Array.from(root.querySelectorAll('[data-fp-form]'));
    const buttons=Array.from(root.querySelectorAll('[data-fp-buy],[data-fp-quick-buy]'));
    const select=root.querySelector('[data-fp-variant]');
    let busy=false,available=buttons.some(b=>!b.disabled);
    const original=new Map(buttons.map(b=>[b,b.querySelector('span')?.textContent||b.textContent]));
    select?.addEventListener('change',()=>{
      const option=select.selectedOptions[0];if(!option)return;
      root.querySelectorAll('[data-fp-id]').forEach(i=>{i.value=option.value;});
      root.querySelectorAll('[data-fp-price]').forEach(n=>{n.textContent=option.dataset.price;});
      const price=Number(option.dataset.priceRaw),compare=Number(option.dataset.compareRaw);
      root.querySelectorAll('[data-fp-compare]').forEach(n=>{n.textContent=option.dataset.compare;n.hidden=compare<=price;});
      root.querySelectorAll('[data-fp-discount]').forEach(n=>{n.textContent=compare>price?`${Math.round((compare-price)/compare*100)}% off`:'';n.hidden=compare<=price;});
      root.querySelectorAll('[data-fp-sale-message]').forEach(n=>{const deadline=n.dataset.saleEnd;n.hidden=compare<=price||(deadline&&!(Date.parse(deadline)>Date.now()));});
      root.querySelectorAll('[data-fp-countdown]').forEach(n=>{n.dataset.fpDiscount=String(compare>price);if(compare<=price)n.hidden=true;});
      available=option.dataset.available==='true';
      buttons.forEach(b=>{b.disabled=!available||busy;(b.querySelector('span')||b).textContent=available?original.get(b):'Sold out';});
    });
    const base=(root.dataset.rootUrl||window.Shopify?.routes?.root||'/').replace(/\/?$/,'/');
    forms.forEach(form=>form.addEventListener('submit',async e=>{
      e.preventDefault();if(busy||!available)return;
      const id=form.querySelector('[name="id"]')?.value;if(!id)return;
      busy=true;root.querySelectorAll('[data-fp-error]').forEach(n=>{n.hidden=true;});buttons.forEach(b=>{b.disabled=true;});if(select)select.disabled=true;
      const submitter=e.submitter||form.querySelector('[data-fp-buy]');const label=submitter?.querySelector('span')||submitter;const old=label?.textContent;if(label)label.textContent='Opening checkout…';
      try{
        const result=await fetch(base+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:[{id,quantity:1}]})});
        if(!result.ok){const detail=await result.json().catch(()=>({}));throw new Error(detail.description||detail.message||'Could not add the bundle. Please try again.');}
        window.location.assign(base+'checkout');
      }catch(err){
        const error=form.querySelector('[data-fp-error]')||root.querySelector('[data-fp-error]');
        if(error){error.textContent=err instanceof TypeError?'Connection interrupted. Check your cart before trying again.':err.message;const link=document.createElement('a');link.href=base+'cart';link.textContent=' View cart';error.appendChild(link);error.hidden=false;error.scrollIntoView({block:'nearest',behavior});}
        busy=false;buttons.forEach(b=>{b.disabled=!available;});if(select)select.disabled=false;if(label)label.textContent=old;
      }
    }));
    root.querySelector('[data-fp-quick-buy]')?.addEventListener('click',()=>{const form=forms[forms.length-1],button=form?.querySelector('[data-fp-buy]');if(form&&button&&!button.disabled)form.requestSubmit(button);});
    const sticky=root.querySelector('[data-fp-sticky]');
    const updateSticky=()=>{
      if(!sticky||!forms.length)return;
      const hero=forms[0].getBoundingClientRect(),offer=forms[forms.length-1].getBoundingClientRect();
      sticky.hidden=hero.bottom>0||(offer.top<innerHeight-65&&offer.bottom>0);
    };
    if(sticky){let scrollFrame;addEventListener('scroll',()=>{cancelAnimationFrame(scrollFrame);scrollFrame=requestAnimationFrame(updateSticky);},{passive:true});addEventListener('resize',updateSticky);updateSticky();}
    const countdown=root.querySelector('[data-fp-countdown]');
    if(countdown){const end=Date.parse(countdown.dataset.fpCountdown);const tick=()=>{const seconds=Math.floor((end-Date.now())/1000);if(!Number.isFinite(seconds)||seconds<=0){countdown.hidden=true;root.querySelectorAll('[data-fp-sale-message]').forEach(n=>{n.hidden=true;});return false;}countdown.querySelector('[data-fp-countdown-value]').textContent=`${Math.floor(seconds/3600)}h ${String(Math.floor(seconds%3600/60)).padStart(2,'0')}m ${String(seconds%60).padStart(2,'0')}s`;countdown.hidden=countdown.dataset.fpDiscount==='false';return true;};if(tick()){const timer=setInterval(()=>{if(!tick())clearInterval(timer);},1000);}}
  };
  const start=()=>document.querySelectorAll('[data-fp-root]').forEach(init);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start);else start();
  document.addEventListener('shopify:section:load',e=>e.target.querySelectorAll('[data-fp-root]').forEach(init));
})();
